import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyApplications, withdrawApplication } from '../../services/jobs';
import type { JobApplication, ApplicationStatus } from '../../types/job';
import { APPLICATION_STATUS_LABELS, JOB_TYPE_LABELS, LOCATION_TYPE_LABELS } from '../../types/job';

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  viewed: 'bg-blue-100 text-blue-800',
  reviewing: 'bg-blue-100 text-blue-800',
  shortlisted: 'bg-green-100 text-green-800',
  interviewing: 'bg-purple-100 text-purple-800',
  offered: 'bg-green-200 text-green-900',
  accepted: 'bg-green-300 text-green-900',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-100 text-gray-600',
};

export function ApplicationsPage() {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<JobApplication[]>([]);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredApplications(applications);
    } else {
      setFilteredApplications(applications.filter(app => app.status === statusFilter));
    }
  }, [applications, statusFilter]);

  const loadApplications = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getMyApplications();
      setApplications(data);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setError('Failed to load your applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId: string) => {
    if (!confirm('Are you sure you want to withdraw this application?')) return;

    setWithdrawingId(applicationId);

    try {
      await withdrawApplication(applicationId);
      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId ? { ...app, status: 'withdrawn' as ApplicationStatus } : app
        )
      );
    } catch (err) {
      console.error('Failed to withdraw application:', err);
      alert('Failed to withdraw application. Please try again.');
    } finally {
      setWithdrawingId(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusCounts = () => {
    const counts: Record<string, number> = { all: applications.length };
    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">My Applications</h1>
          <p className="text-gray-600 mt-1">Track and manage your job applications</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
            <button onClick={loadApplications} className="ml-2 underline">
              Try again
            </button>
          </div>
        )}

        {/* Status filter tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4 -mb-px">
            {(['all', 'pending', 'reviewing', 'shortlisted', 'interviewing', 'offered', 'rejected', 'withdrawn'] as const).map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  statusFilter === status
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {status === 'all' ? 'All' : APPLICATION_STATUS_LABELS[status]}
                {statusCounts[status] ? (
                  <span className="ml-1 text-xs text-gray-400">({statusCounts[status]})</span>
                ) : null}
              </button>
            ))}
          </nav>
        </div>

        {/* Applications list */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't applied to any jobs yet."
                : `No applications with status "${APPLICATION_STATUS_LABELS[statusFilter as ApplicationStatus]}".`}
            </p>
            <Link
              to="/jobs"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
            >
              Browse Jobs
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map(application => (
              <div
                key={application.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {application.job?.company_logo ? (
                      <img
                        src={application.job.company_logo}
                        alt={application.job.company_name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-semibold">
                        {application.job?.company_name?.charAt(0) || 'C'}
                      </div>
                    )}
                    <div>
                      <Link
                        to={`/jobs/${application.job?.slug || application.job_id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                      >
                        {application.job?.title || 'Job Title'}
                      </Link>
                      <p className="text-sm text-gray-600">
                        {application.job?.company_name || 'Company'}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {application.job?.job_type && (
                          <span className="text-xs text-gray-500">
                            {JOB_TYPE_LABELS[application.job.job_type]}
                          </span>
                        )}
                        {application.job?.location_type && (
                          <span className="text-xs text-gray-500">
                            · {LOCATION_TYPE_LABELS[application.job.location_type]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Status badge */}
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[application.status]}`}
                    >
                      {APPLICATION_STATUS_LABELS[application.status]}
                    </span>

                    {/* Messages indicator */}
                    {application.unread_count && application.unread_count > 0 && (
                      <Link
                        to={`/expert/applications/${application.id}/messages`}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                          <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                        </svg>
                        {application.unread_count} new
                      </Link>
                    )}
                  </div>
                </div>

                {/* Timeline / status dates */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-6 text-xs text-gray-500">
                    <span>Applied: {formatDate(application.created_at)}</span>
                    {application.viewed_at && (
                      <span>Viewed: {formatDate(application.viewed_at)}</span>
                    )}
                    {application.shortlisted_at && (
                      <span>Shortlisted: {formatDate(application.shortlisted_at)}</span>
                    )}
                    {application.interviewed_at && (
                      <span>Interview: {formatDate(application.interviewed_at)}</span>
                    )}
                    {application.offered_at && (
                      <span>Offer: {formatDate(application.offered_at)}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center gap-4">
                  <Link
                    to={`/jobs/${application.job?.slug || application.job_id}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Job
                  </Link>
                  <Link
                    to={`/expert/applications/${application.id}/messages`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Messages
                  </Link>
                  {!['withdrawn', 'rejected', 'accepted'].includes(application.status) && (
                    <button
                      onClick={() => handleWithdraw(application.id)}
                      disabled={withdrawingId === application.id}
                      className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      {withdrawingId === application.id ? 'Withdrawing...' : 'Withdraw'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
